import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { portalApi, apiError } from "../lib/api";
import { usePortalAuth } from "../context/PortalAuth";
import { LoyaltyCard } from "../components/LoyaltyCard";
import { Home, CreditCard, Gift, History, User, Plus, Minus, LogOut, Sparkles, Ticket, Tag, TrendingUp } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso; }
}

export default function CustomerPortal() {
  const nav = useNavigate();
  const { customer, setCustomer, logout } = usePortalAuth();
  const [tab, setTab] = useState("home");
  const [me, setMe] = useState(null);
  const [history, setHistory] = useState([]);
  const [rewards, setRewards] = useState(null);

  const load = useCallback(async () => {
    try {
      const [m, h, r] = await Promise.all([
        portalApi.get("/portal/me"),
        portalApi.get("/portal/history"),
        portalApi.get("/portal/rewards"),
      ]);
      setMe(m.data);
      setCustomer(m.data.customer);
      setHistory(h.data);
      setRewards(r.data);
    } catch (e) { toast.error(apiError(e)); }
  }, [setCustomer]);

  useEffect(() => {
    if (customer === false) { nav("/portal"); return; }
    if (customer) load();
  }, [customer, load, nav]);

  // poll for real-time points updates
  useEffect(() => {
    const iv = setInterval(() => { if (localStorage.getItem("vs_customer_token")) load(); }, 20000);
    return () => clearInterval(iv);
  }, [load]);

  if (!me || !customer) return <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center text-stone-400">Lädt…</div>;

  const c = me.customer;
  const nextTier = me.next_tier;

  const doLogout = () => { logout(); nav("/portal"); };

  const updateMarketing = async (v) => {
    try {
      await portalApi.put("/portal/marketing", { marketing_consent: v });
      setMe({ ...me, customer: { ...c, marketing_consent: v } });
      toast.success(v ? "Marketing-E-Mails aktiviert" : "Marketing-E-Mails deaktiviert");
    } catch (e) { toast.error(apiError(e)); }
  };

  const tabs = [
    { id: "home", icon: Home, label: "Start" },
    { id: "card", icon: CreditCard, label: "Karte" },
    { id: "rewards", icon: Gift, label: "Prämien" },
    { id: "history", icon: History, label: "Verlauf" },
    { id: "profile", icon: User, label: "Profil" },
  ];

  return (
    <div className="max-w-md mx-auto min-h-screen bg-[#FAF7F2] pb-24 shadow-2xl relative border-x border-stone-200/60">
      {/* Header */}
      <div className="px-5 pt-6 pb-4 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[#4A1D24]">
          <Sparkles className="h-4 w-4 text-[#C59B6C]" />
          <span className="text-xs tracking-[0.3em] font-semibold">VELOURS &amp; SILK</span>
        </div>
        <button data-testid="portal-logout-button" onClick={doLogout} className="text-stone-400 hover:text-stone-700"><LogOut className="h-5 w-5" /></button>
      </div>

      <div className="px-5 space-y-5">
        {tab === "home" && (
          <div className="space-y-5" data-testid="portal-home-tab">
            <div>
              <p className="text-sm text-stone-500">Willkommen zurück,</p>
              <h1 className="font-serif text-3xl text-stone-900">{c.first_name}</h1>
            </div>
            <LoyaltyCard customer={c} />
            <div className="rounded-2xl bg-white/90 border border-stone-200/80 shadow-sm p-5">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-widest text-stone-400">Punktestand</p>
                <span className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full border border-amber-400/50 bg-amber-500/15 text-amber-900">{c.tier}</span>
              </div>
              <p className="font-mono text-4xl font-bold text-[#4A1D24] mt-1" data-testid="customer-points-balance">{c.points}</p>
              {nextTier && (
                <div className="mt-3">
                  <div className="flex items-center justify-between text-xs text-stone-500 mb-1">
                    <span className="flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Nächste Stufe: {nextTier.name}</span>
                    <span>noch {nextTier.points_needed} P</span>
                  </div>
                  <Progress value={Math.max(5, 100 - (nextTier.points_needed / (c.points + nextTier.points_needed)) * 100)} className="h-2" />
                </div>
              )}
            </div>
            {rewards?.offers?.length > 0 && (
              <div>
                <h2 className="font-serif text-xl text-stone-900 mb-2">Aktuelle Angebote</h2>
                <div className="space-y-2" data-testid="portal-offers">
                  {rewards.offers.map((o) => (
                    <div key={o.id} className="p-4 rounded-2xl bg-[#4A1D24] text-stone-100 shadow-sm">
                      <div className="flex items-center gap-2 text-[#E7C79A]"><Tag className="h-4 w-4" /><p className="font-medium">{o.title}</p></div>
                      <p className="text-sm text-stone-300/80 mt-1">{o.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "card" && (
          <div className="space-y-4 pt-2" data-testid="portal-card-tab">
            <h1 className="font-serif text-3xl text-stone-900">Meine Karte</h1>
            <LoyaltyCard customer={c} />
            <div className="rounded-2xl bg-white/90 border border-stone-200/80 shadow-sm p-5 text-center">
              <p className="text-sm text-stone-600">Zeigen Sie diesen QR-Code beim nächsten Besuch vor. Die Mitarbeiterin scannt ihn und findet Sie sofort im System.</p>
              <p className="text-xs text-stone-400 mt-2">Der Code enthält keine persönlichen Daten.</p>
            </div>
          </div>
        )}

        {tab === "rewards" && rewards && (
          <div className="space-y-5 pt-2" data-testid="portal-rewards-tab">
            <h1 className="font-serif text-3xl text-stone-900">Prämien</h1>

            <section>
              <h2 className="text-xs font-semibold uppercase tracking-widest text-amber-800/80 mb-2">Verfügbare Prämien</h2>
              <div className="space-y-2">
                {rewards.available.map((r) => {
                  const ok = c.points >= r.points_cost;
                  return (
                    <div key={r.id} data-testid={`reward-${r.id}`} className="p-4 rounded-2xl bg-white border border-stone-200/80 shadow-sm flex justify-between items-center">
                      <div className="min-w-0 pr-3">
                        <p className="font-medium text-stone-900">{r.name}</p>
                        <p className="text-xs text-stone-500">{r.description}</p>
                      </div>
                      <span className={`shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full border ${ok ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-stone-100 text-stone-500 border-stone-200"}`}>{r.points_cost} P</span>
                    </div>
                  );
                })}
              </div>
            </section>

            {rewards.vouchers.length > 0 && (
              <section>
                <h2 className="text-xs font-semibold uppercase tracking-widest text-amber-800/80 mb-2">Aktive Gutscheine</h2>
                <div className="space-y-2" data-testid="portal-vouchers">
                  {rewards.vouchers.map((v) => (
                    <div key={v.id} className="p-4 rounded-2xl bg-white border border-dashed border-[#C59B6C] shadow-sm flex items-center gap-3">
                      <Ticket className="h-5 w-5 text-[#C59B6C]" />
                      <div className="flex-1">
                        <p className="font-mono font-semibold text-[#4A1D24]">{v.code}</p>
                        <p className="text-xs text-stone-500">{v.description}</p>
                      </div>
                      <span className="text-[10px] text-stone-400">bis {v.expires_at}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section>
              <h2 className="text-xs font-semibold uppercase tracking-widest text-amber-800/80 mb-2">Eingelöste Prämien</h2>
              <div className="space-y-2" data-testid="portal-redeemed">
                {rewards.redeemed.length === 0 && <p className="text-sm text-stone-400">Noch keine Prämien eingelöst.</p>}
                {rewards.redeemed.map((r) => (
                  <div key={r.id} className="p-3.5 rounded-xl bg-white/70 border border-stone-100 flex justify-between items-center">
                    <div><p className="text-sm font-medium text-stone-800">{r.reward_name}</p><p className="text-xs text-stone-400">{fmt(r.redeemed_at)}</p></div>
                    <span className="text-xs text-rose-700 font-semibold">−{r.points} P</span>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}

        {tab === "history" && (
          <div className="space-y-4 pt-2" data-testid="points-history-timeline">
            <h1 className="font-serif text-3xl text-stone-900">Punkteverlauf</h1>
            {history.length === 0 && <p className="text-sm text-stone-400">Noch keine Aktivität.</p>}
            <div className="space-y-2">
              {history.map((t) => (
                <div key={t.id} className="flex items-start gap-4 p-3.5 rounded-xl bg-white/80 border border-stone-100 shadow-sm" data-testid={`history-item-${t.id}`}>
                  <div className={`shrink-0 h-9 w-9 rounded-full flex items-center justify-center ${t.type === "earn" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                    {t.type === "earn" ? <Plus className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-stone-900">{t.type === "earn" ? "+" : "−"}{t.amount} Punkte</p>
                    <p className="text-sm text-stone-500">{t.type === "earn" ? "Punkte erhalten" : "Punkte eingelöst"}</p>
                    <p className="text-xs text-stone-400 mt-0.5">{fmt(t.created_at)} · Stand: {t.balance_after} P</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "profile" && (
          <div className="space-y-4 pt-2" data-testid="portal-profile-tab">
            <h1 className="font-serif text-3xl text-stone-900">Profil</h1>
            <div className="rounded-2xl bg-white/90 border border-stone-200/80 shadow-sm p-5 space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-stone-500">Name</span><span className="font-medium text-stone-800">{c.first_name} {c.last_name}</span></div>
              <div className="flex justify-between"><span className="text-stone-500">E-Mail</span><span className="font-medium text-stone-800 truncate max-w-[60%]">{c.email}</span></div>
              <div className="flex justify-between"><span className="text-stone-500">Mitgliedschaft</span><span className="font-medium text-stone-800">{c.tier}</span></div>
              <div className="flex justify-between"><span className="text-stone-500">Mitglied seit</span><span className="font-medium text-stone-800">{fmt(c.created_at)}</span></div>
            </div>

            <div className="rounded-2xl bg-white/90 border border-stone-200/80 shadow-sm p-5">
              <h2 className="font-semibold text-stone-900">E-Mail-Einstellungen</h2>
              <div className="flex items-center justify-between mt-3 py-2 border-b border-stone-100">
                <div className="pr-4">
                  <p className="text-sm font-medium text-stone-800">Konto- &amp; Punktebenachrichtigungen</p>
                  <p className="text-xs text-stone-400">Wichtige Mitteilungen (immer aktiv)</p>
                </div>
                <Switch checked disabled data-testid="account-emails-switch" />
              </div>
              <div className="flex items-center justify-between mt-2 py-2">
                <div className="pr-4">
                  <p className="text-sm font-medium text-stone-800">Marketing &amp; Angebote</p>
                  <p className="text-xs text-stone-400">Nur mit Ihrer Einwilligung</p>
                </div>
                <Switch data-testid="marketing-consent-toggle" checked={c.marketing_consent} onCheckedChange={updateMarketing} />
              </div>
            </div>

            <button data-testid="profile-logout-button" onClick={doLogout} className="w-full py-3 rounded-xl border border-stone-200 text-stone-600 text-sm font-medium hover:bg-stone-50 flex items-center justify-center gap-2">
              <LogOut className="h-4 w-4" /> Abmelden
            </button>
          </div>
        )}
      </div>

      {/* Bottom nav */}
      <nav className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md bg-white/95 backdrop-blur-md border-t border-stone-200 px-2 py-2 flex justify-around" data-testid="portal-bottom-nav">
        {tabs.map((t) => {
          const I = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} data-testid={`nav-${t.id}`} onClick={() => setTab(t.id)} className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg transition-colors ${active ? "text-[#4A1D24]" : "text-stone-400"}`}>
              <I className="h-5 w-5" />
              <span className="text-[10px] font-medium">{t.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
